import { calculateDistance } from './geo';
import { formatElapsedDuration } from './tenMinutePoints';

import type { MapCommentDraftAnchor } from './mapCommentPreview';

export type CommentTrackSnapPoint = Readonly<{
  lat: number;
  lon: number;
  utc?: string | null;
  date?: string | null;
}>;

const MS_PER_HOUR = 60 * 60 * 1000;

const toTrackTimestampMs = (value: string | null | undefined): number | null => {
  const normalized = toTrackUtcIso(value);
  if (!normalized) return null;
  const timestamp = Date.parse(normalized);
  return Number.isFinite(timestamp) ? timestamp : null;
};

/**
 * Resolves a temporal comment against the currently loaded track and formats
 * it with the same elapsed-time rules used by the ten-minute labels.
 * Returning null deliberately means “do not render this comment”: the anchor
 * belongs to a point that is not part of the loaded track selection.
 */
export const getMapCommentRelativeTime = ({
  anchorUtc,
  track,
}: {
  anchorUtc: string | null | undefined;
  track: readonly CommentTrackSnapPoint[];
}): string | null => {
  const anchorTimestamp = toTrackTimestampMs(anchorUtc);
  if (anchorTimestamp === null) return null;

  const timestamps = track
    .filter(
      (point) =>
        Number.isFinite(point.lat) &&
        Number.isFinite(point.lon) &&
        Math.abs(point.lat) <= 90 &&
        Math.abs(point.lon) <= 180,
    )
    .map((point) => toTrackTimestampMs(point.utc) ?? toTrackTimestampMs(point.date))
    .filter((timestamp): timestamp is number => timestamp !== null)
    .sort((left, right) => left - right);
  if (timestamps.length === 0 || !timestamps.includes(anchorTimestamp)) return null;

  const startTimestamp = timestamps[0];
  const endTimestamp = timestamps.at(-1);
  if (endTimestamp === undefined) return null;
  const useHourFormat = endTimestamp - startTimestamp >= MS_PER_HOUR;
  return formatElapsedDuration(anchorTimestamp - startTimestamp, useHourFormat);
};

/**
 * Track tables store UTC timestamps without a timezone. Treat a timezone-less
 * value as UTC rather than letting the browser reinterpret it in local time.
 */
export const toTrackUtcIso = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const hasTimeZone = /(?:z|[+-]\d{2}(?::?\d{2})?)$/i.test(trimmed);
  const timestamp = hasTimeZone ? trimmed : `${trimmed.replace(' ', 'T')}Z`;
  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

/**
 * Resolves a dragged comment pin to a real, recorded point of the active
 * track. Keeping the exact track sample avoids showing an interpolated point
 * that would move again when a race-time anchor is validated by the API.
 */
export const snapMapCommentAnchorToTrack = ({
  anchorType,
 lat,
 lon,
 track,
  maxDistanceKm,
}: {
  anchorType: MapCommentDraftAnchor['anchorType'];
  lat: number;
  lon: number;
  track: readonly CommentTrackSnapPoint[];
  /** Optional capture radius. Without it the nearest track point is returned. */
  maxDistanceKm?: number;
}): MapCommentDraftAnchor | null => {
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lon) ||
    Math.abs(lat) > 90 ||
    Math.abs(lon) > 180 ||
    (maxDistanceKm !== undefined && (!Number.isFinite(maxDistanceKm) || maxDistanceKm < 0))
  ) {
    return null;
  }

  let closest: CommentTrackSnapPoint | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (const point of track) {
    if (
      !Number.isFinite(point.lat) ||
      !Number.isFinite(point.lon) ||
      Math.abs(point.lat) > 90 ||
      Math.abs(point.lon) > 180
    ) {
      continue;
    }
    const distance = calculateDistance(lat, lon, point.lat, point.lon);
    if (distance < closestDistance) {
      closest = point;
      closestDistance = distance;
    }
  }

  if (!closest || (maxDistanceKm !== undefined && closestDistance > maxDistanceKm)) return null;

  const anchorUtc = toTrackUtcIso(closest.utc) ?? toTrackUtcIso(closest.date);
  if (anchorType === 'race_time' && anchorUtc) {
    return {
      anchorType: 'race_time',
      anchorUtc,
      anchorLat: closest.lat,
      anchorLon: closest.lon,
    };
  }

  // A historical comment with an invalid timestamp remains movable. It becomes
  // a spatial anchor rather than saving a coordinate/time pair that disagrees.
  return {
    anchorType: 'map_point',
    anchorUtc: null,
    anchorLat: closest.lat,
    anchorLon: closest.lon,
  };
};
